import { useEffect, useState, type FormEvent } from "react";
import { api, FalhaApi, type CategoriaLembrete, type LembreteApi } from "../api";

interface FormularioLembrete {
  titulo: string;
  data: string;
  hora: string;
  categoria: CategoriaLembrete;
  observacoes: string;
}

const FORM_VAZIO: FormularioLembrete = {
  titulo: "",
  data: "",
  hora: "",
  categoria: "GERAL",
  observacoes: "",
};

const RETULO_STATUS_ALERTA: Record<string, string> = {
  PENDENTE: "pendente",
  ENVIADO: "enviado",
  FALHOU: "falhou",
  CANCELADO: "cancelado",
};

export function Lembretes() {
  const [lembretes, setLembretes] = useState<LembreteApi[] | null>(null);
  const [form, setForm] = useState<FormularioLembrete>(FORM_VAZIO);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function carregar() {
    api
      .listarLembretes()
      .then(({ lembretes }) => setLembretes(lembretes))
      .catch(() => setLembretes([]));
  }

  useEffect(carregar, []);

  function iniciarEdicao(lembrete: LembreteApi) {
    setEditandoId(lembrete.id);
    setForm({
      titulo: lembrete.titulo,
      data: lembrete.data.slice(0, 10),
      hora: lembrete.hora ?? "",
      categoria: lembrete.categoria,
      observacoes: lembrete.observacoes ?? "",
    });
  }

  function cancelarEdicao() {
    setEditandoId(null);
    setForm(FORM_VAZIO);
  }

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    const dados = {
      titulo: form.titulo,
      data: form.data,
      hora: form.hora || null,
      categoria: form.categoria,
      observacoes: form.observacoes || null,
    };
    try {
      if (editandoId) {
        await api.atualizarLembrete(editandoId, dados);
      } else {
        await api.criarLembrete(dados);
      }
      cancelarEdicao();
      carregar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao salvar o lembrete.");
    } finally {
      setSalvando(false);
    }
  }

  async function excluir(id: string) {
    try {
      await api.excluirLembrete(id);
      carregar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao apagar o lembrete.");
    }
  }

  return (
    <div className="pagina">
      <h1>Lembretes</h1>

      <form className="cartao formulario-lembrete" onSubmit={aoEnviar}>
        <h2>{editandoId ? "Editar lembrete" : "Novo lembrete"}</h2>

        {erro && (
          <div className="alerta alerta-erro" role="alert">
            {erro}
          </div>
        )}

        <label htmlFor="titulo">Título</label>
        <input
          id="titulo"
          type="text"
          value={form.titulo}
          onChange={(e) => setForm({ ...form, titulo: e.target.value })}
          placeholder="ex.: Consulta Dentista - Miguel"
          required
        />

        <div className="linha-campos">
          <div>
            <label htmlFor="data">Data</label>
            <input
              id="data"
              type="date"
              value={form.data}
              onChange={(e) => setForm({ ...form, data: e.target.value })}
              required
            />
          </div>
          <div>
            <label htmlFor="hora">Hora (opcional)</label>
            <input
              id="hora"
              type="time"
              value={form.hora}
              onChange={(e) => setForm({ ...form, hora: e.target.value })}
            />
          </div>
        </div>

        <label htmlFor="categoria">Categoria</label>
        <select
          id="categoria"
          value={form.categoria}
          onChange={(e) => setForm({ ...form, categoria: e.target.value as CategoriaLembrete })}
        >
          <option value="GERAL">Geral (alerta na véspera e no dia)</option>
          <option value="IMPORTANTE">Importante (alerta 2 dias antes também)</option>
        </select>

        <label htmlFor="observacoes">Observações (opcional)</label>
        <textarea
          id="observacoes"
          value={form.observacoes}
          onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
          rows={2}
        />

        <div className="linha-botoes">
          <button className="botao botao-primario" type="submit" disabled={salvando}>
            {salvando ? "Salvando…" : editandoId ? "Salvar alterações" : "Adicionar lembrete"}
          </button>
          {editandoId && (
            <button className="botao botao-texto" type="button" onClick={cancelarEdicao}>
              Cancelar
            </button>
          )}
        </div>
      </form>

      <section className="lista-lembretes">
        {lembretes === null && <p>Carregando…</p>}
        {lembretes?.length === 0 && <p>Nenhum lembrete cadastrado ainda.</p>}
        {lembretes?.map((lembrete) => (
          <article key={lembrete.id} className="cartao cartao-lembrete">
            <div className="cabecalho-lembrete">
              <strong>{lembrete.titulo}</strong>
              {lembrete.categoria === "IMPORTANTE" && <span className="etiqueta">importante</span>}
              <span className="etiqueta etiqueta-origem">
                {lembrete.origem === "WHATSAPP" ? "via WhatsApp" : "via site"}
              </span>
            </div>
            <p>
              {lembrete.data.slice(8, 10)}/{lembrete.data.slice(5, 7)}/{lembrete.data.slice(0, 4)}
              {lembrete.hora ? ` às ${lembrete.hora}` : ""}
            </p>
            {lembrete.observacoes && <p className="observacoes-lembrete">{lembrete.observacoes}</p>}
            <p className="status-alertas">
              {lembrete.alertas.map((a) => `${a.tipo}: ${RETULO_STATUS_ALERTA[a.status]}`).join(" · ")}
            </p>
            <div className="linha-botoes">
              <button className="botao botao-texto" onClick={() => iniciarEdicao(lembrete)}>
                Editar
              </button>
              <button className="botao botao-texto" onClick={() => void excluir(lembrete.id)}>
                Apagar
              </button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
